// useUrlState — the one shared hook family that owns view-state serialization
// (filters / sort / pagination / view-mode / open peek), per ADR-037. View
// state lives in the URL query string; this layer + its codec set are the only
// place that reads or writes it — pages and controllers call `useUrlState`,
// never `setSearchParams` or nuqs directly.
//
// Built on nuqs: typed parsers, default-omission, history control, and a shared
// update queue that coalesces concurrent writes into one history entry (the
// lost-update / N-entries class a hand-rolled useSearchParams hook gets wrong).
// The codecs bake our house rules:
//   - write on commit  — auto-apply controls commit per change under REPLACE
//     history (so Back is never spammed); deferred-apply forms (later) commit
//     on Apply. Never serialize a draft/mid-typing value as new history.
//   - omit defaults     — empty string / [] / false / the default sort never serialize
//   - multi-value = comma  (?status=active,pending)
//   - refine-the-view writes REPLACE history (filters/sort/search/page/grouping
//     are the slots here); an overlay held in the link (the peek, the open file, an
//     import run) moves its ONE history step through `useUrlOverlay` (open · move ·
//     close), never through a slot's history option
//   - tolerant reads    — malformed/unknown values fall back to the default, never throw
// A period WORD stays a word in the URL (rolling — the server re-evaluates it when
// the link opens); two DAYS stay fixed; one key per time axis (ADR-082 §5). The
// codec carries the value and never resolves a word.

import {
  useQueryStates,
  parseAsString,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  createParser,
  type UseQueryStatesKeysMap,
  type UseQueryStatesReturn,
} from 'nuqs';

/** Single-column sort. Serialized as `name` (ascending) / `-name` (descending). */
export interface SortValue {
  id: string;
  desc: boolean;
}

const sortParser = createParser<SortValue>({
  parse(value) {
    const desc = value.startsWith('-');
    const id = desc ? value.slice(1) : value;
    return id ? { id, desc } : null;
  },
  serialize(value) {
    return value.desc ? `-${value.id}` : value.id;
  },
  eq(a, b) {
    return a.id === b.id && a.desc === b.desc;
  },
});

/**
 * The view-state codec set — the typed slots a list (or any URL-bound view)
 * declares. Each bakes our house rules; compose them into a keymap for
 * `useUrlState`.
 */
const pageParser = createParser<number>({
  parse: (value) => {
    const n = parseAsInteger.parse(value);
    return n === null ? null : Math.max(1, n);
  },
  serialize: (n) => String(n),
});

export const url = {
  /** Free text (e.g. search). Auto-applies per change under replace history (no Back-spam); empty is omitted. A server-side search adds debounce + shallow:false at its slice. */
  text: () => parseAsString.withDefault(''),
  /** Multi-select, comma-joined (`?k=a,b`); an empty selection is omitted. */
  multi: () => parseAsArrayOf(parseAsString, ',').withDefault([]),
  /** Boolean flag; `false` is omitted. */
  bool: () => parseAsBoolean.withDefault(false),
  /** Single-column sort (`name` / `-name`). A default sort is omitted from the URL until changed. */
  sort: (defaultSort?: SortValue) => (defaultSort ? sortParser.withDefault(defaultSort) : sortParser),
  /** 1-based page index (server-paginated lists) — read tolerantly: `?page=0`, a negative or a non-number is page 1
   *  (a hand-edited or stale link never sends a negative offset — ADR-037's tolerant reads). */
  page: () => pageParser.withDefault(1),
};

/**
 * Read + write a set of URL view-state slots. The single entry point for view
 * state — the codecs above carry each slot's policy (history mode, debounce,
 * default-omission), so this stays a thin, typed choke point over nuqs rather
 * than a per-page `setSearchParams` call.
 */
export function useUrlState<KeyMap extends UseQueryStatesKeysMap>(
  keyMap: KeyMap,
): UseQueryStatesReturn<KeyMap> {
  return useQueryStates(keyMap);
}
